import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Form, Input, Typography } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { useLogin } from '@presentation/hooks/useAuth';
import { ValidationError } from '@core/errors/AppError';
import { setAntdFormErrors } from '@shared/utils/formErrors';
import { paths } from '@app/routes/paths';
import { APP_NAME } from '@shared/constants';

interface LoginFormValues {
  email: string;
  password: string;
  remember: boolean;
}

export const LoginPage = () => {
  const navigate = useNavigate();
  const login = useLogin();
  const [form] = Form.useForm<LoginFormValues>();

  const handleFinish = (values: LoginFormValues) => {
    login.mutate(values, {
      onSuccess: (outcome) => {
        if (outcome.status === 'two_factor_required') {
          navigate(paths.twoFactorChallenge, { state: { challengeToken: outcome.challengeToken } });
          return;
        }
        navigate(paths.dashboard, { replace: true });
      },
      onError: (error) => {
        if (error instanceof ValidationError) {
          setAntdFormErrors(form, error.errors);
        }
      },
    });
  };

  const generalError =
    login.error && !(login.error instanceof ValidationError) ? login.error.message : null;

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-sm">
        <img src="/apilab-icon-64.png" alt="" width={40} height={40} className="mb-2" />
        <Typography.Title level={4} className="!mb-1">
          Sign in
        </Typography.Title>
        <Typography.Text type="secondary">Welcome back to {APP_NAME}.</Typography.Text>

        <Form
          form={form}
          layout="vertical"
          className="mt-6"
          initialValues={{ remember: false }}
          onFinish={handleFinish}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, message: 'Email is required' }, { type: 'email' }]}
          >
            <Input prefix={<MailOutlined />} autoComplete="email" size="large" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Password is required' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              autoComplete="current-password"
              size="large"
            />
          </Form.Item>

          <div className="mb-4 flex items-center justify-between">
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox>Remember me</Checkbox>
            </Form.Item>
            <Link to={paths.forgotPassword}>Forgot password?</Link>
          </div>

          {generalError && <Alert type="error" showIcon title={generalError} className="mb-4" />}

          <Button type="primary" htmlType="submit" size="large" block loading={login.isPending}>
            Sign in
          </Button>

          <div className="mt-4 text-center">
            <Typography.Text type="secondary">No account? </Typography.Text>
            <Link to={paths.register}>Create one</Link>
          </div>
        </Form>
      </Card>
    </div>
  );
};
